import { useCallback, useEffect, useMemo, useState } from 'react';

import { CheckIcon, ChevronsUpDownIcon, XIcon } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useDebounce } from 'use-debounce';

import { Person } from '@/../../backend/src/matter/person/person.entity';
import { useGetPersonNames } from '@/api/queries/useGetPersonNames';
import { useGetSubsetPersons } from '@/api/queries/useGetSubsetPersons';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/new/components/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

const PAGE_SIZE = 50;

interface PersonMultiSelectProps {
  values: string[];
  onValuesChange: (values: string[]) => void;
  matterId: string;
  subsetId: string;
}

export function PersonMultiSelect({ values, onValuesChange, matterId, subsetId }: PersonMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const [loadedPersons, setLoadedPersons] = useState<Person[]>([]);
  const [hasMore, setHasMore] = useState(false);

  // Track selected person names so badges show correctly even when not in current search results
  const [selectedNames, setSelectedNames] = useState<Map<string, string>>(new Map());

  // Resolve names for pre-existing selections (e.g. dialog reopened with active filters)
  const { data: resolvedNames } = useGetPersonNames(matterId, subsetId, values);
  useEffect(() => {
    if (!resolvedNames) return;
    setSelectedNames((prev) => {
      const next = new Map(prev);
      for (const { id, name } of resolvedNames) {
        next.set(id, name);
      }
      return next;
    });
  }, [resolvedNames]);

  const { data, isFetching } = useGetSubsetPersons({
    matterId,
    subsetId,
    searchQuery: debouncedSearch,
    searchNameOnly: true,
    page,
    pageSize: PAGE_SIZE,
    paginated: true,
    filters: {},
  });

  // Reset when search changes
  useEffect(() => {
    setPage(1);
    setLoadedPersons([]);
    setHasMore(false);
  }, [debouncedSearch]);

  // Append results when data arrives
  useEffect(() => {
    if (!data) return;
    setLoadedPersons((prev) => {
      if (page === 1) return data.persons;
      // Deduplicate in case of race conditions
      const existingIds = new Set(prev.map((p) => p.id));
      const newPersons = data.persons.filter((p) => !existingIds.has(p.id));
      return [...prev, ...newPersons];
    });
    setHasMore(page < data.pageCount);
  }, [data, page]);

  // Infinite scroll sentinel
  const { ref: sentinelRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  }, [inView, hasMore, isFetching]);

  // Track names for selected items
  useEffect(() => {
    if (!data) return;
    setSelectedNames((prev) => {
      const next = new Map(prev);
      for (const person of data.persons) {
        if (values.includes(person.id)) {
          next.set(person.id, person.name);
        }
      }
      return next;
    });
  }, [data, values]);

  const selectedSet = useMemo(() => new Set(values), [values]);

  const toggleValue = useCallback(
    (personId: string, personName: string) => {
      if (selectedSet.has(personId)) {
        onValuesChange(values.filter((v) => v !== personId));
        setSelectedNames((prev) => {
          const next = new Map(prev);
          next.delete(personId);
          return next;
        });
      } else {
        onValuesChange([...values, personId]);
        setSelectedNames((prev) => new Map(prev).set(personId, personName));
      }
    },
    [values, selectedSet, onValuesChange],
  );

  const clearAll = useCallback(() => {
    onValuesChange([]);
    setSelectedNames(new Map());
  }, [onValuesChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex h-auto min-h-9 w-full items-center justify-between gap-2 overflow-hidden rounded-md border bg-transparent p-1.5 whitespace-nowrap transition-colors duration-150 outline-none hover:bg-gray-50/50 focus-visible:ring focus-visible:ring-teal-500 data-[placeholder]:text-gray-500"
        >
          {values.length === 0 ? (
            <span className="min-w-0 overflow-hidden pl-1.5 font-normal text-gray-500">All entities</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {values.map((id) => (
                <Badge
                  key={id}
                  variant="outline"
                  className="group flex items-center gap-1 rounded-md bg-gray-50 duration-150 hover:border-red-300 hover:bg-red-50 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(id, selectedNames.get(id) ?? id);
                  }}
                >
                  {selectedNames.get(id) ?? id}
                  <XIcon className="text-muted-foreground group-hover:text-destructive -mr-0.75 size-2" />
                </Badge>
              ))}
            </div>
          )}
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search entities..." value={searchInput} onValueChange={setSearchInput} />
          <CommandList>
            {isFetching && loadedPersons.length === 0 && (
              <div className="py-6 text-center text-sm text-gray-400">Searching...</div>
            )}
            {!isFetching && loadedPersons.length === 0 && <CommandEmpty>No entities found</CommandEmpty>}

            {values.length > 0 && (
              <CommandItem
                className="text-destructive sticky top-0 z-10 cursor-pointer bg-white py-3 pl-4 text-gray-500 hover:bg-gray-50"
                onSelect={clearAll}
              >
                <XIcon className="mr-0.5 size-4" />
                Clear all ({values.length})
              </CommandItem>
            )}

            <CommandGroup>
              {loadedPersons.map((person) => {
                const isSelected = selectedSet.has(person.id);
                return (
                  <CommandItem
                    key={person.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onSelect={() => toggleValue(person.id, person.name)}
                  >
                    <CheckIcon className={cn('mr-0.5 ml-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                    {person.name}
                  </CommandItem>
                );
              })}

              {/* Infinite scroll sentinel */}
              {hasMore && (
                <div ref={sentinelRef} className="flex items-center justify-center py-2">
                  <span className="text-xs text-gray-400">Loading more...</span>
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
